'use client';

import { formatRideCapacityLabel } from '@cfixe-monorepo/shared';
import type { BookingPrestation } from '../../types';
import { useBooking } from '../../context/BookingContext';
import { ImageGallery } from './ImageGallery';

interface RideServiceCardProps {
  prestation: BookingPrestation;
  onSelect: (prestation: BookingPrestation) => void;
}

/**
 * Carte « Course » (type RIDE) du tunnel classique — vitrine uniquement, jamais réservable ici :
 * pas d'ajout au panier ni de choix de variante/durée. Son CTA unique bascule vers le flux course
 * (calculateur VTC) via `onSelectRidePrestation`, avec la formule présélectionnée et le trajet
 * par défaut prérempli côté hôte.
 *
 * - Badge « Course » + capacité du pool (formatRideCapacityLabel — helper partagé avec le tunnel
 *   ride et les blocs catalogue des sites publics).
 * - Prix : `ride_estimated_price` (chaîne PRÉ-FORMATÉE backend, jamais reformatée) →
 *   « À partir de {prix} » ; absente → « Prix selon trajet ».
 * - `is_bookable` d'une carte RIDE = véhicule actif + tarif (règle du pool) : false → carte grisée,
 *   CTA remplacé par la raison d'indisponibilité.
 */
export function RideServiceCard({ prestation, onSelect }: RideServiceCardProps) {
  const { labels } = useBooking();

  const isBookable = prestation.is_bookable !== false;
  const capacity = formatRideCapacityLabel(prestation, {
    passengers: labels.ride_capacity_passengers,
    small_luggage: labels.ride_capacity_small_luggage,
    large_luggage: labels.ride_capacity_large_luggage,
  });
  const priceLabel = prestation.ride_estimated_price
    ? labels.ride_price_from.replace('{price}', prestation.ride_estimated_price)
    : labels.ride_price_by_route;

  return (
    <div className={`bt-service-card ${!isBookable ? 'bt-service-card--unavailable' : ''}`}>
      <div className="bt-service-card__header">
        {prestation.pictures && prestation.pictures.length > 0 && (
          <ImageGallery
            pictures={prestation.pictures}
            alt={prestation.name}
            labels={{ close: labels.close, previous: labels.photo_previous, next: labels.photo_next }}
          />
        )}
        <div className="bt-service-card__info">
          <p className="bt-service-card__name">{prestation.name}</p>
          <div className="bt-service-card__meta">
            <span className="bt-service-card__ride-badge">{labels.ride_badge}</span>
            {capacity && (
              <>
                <span className="bt-service-card__sep">·</span>
                <span className="bt-service-card__duration">{capacity}</span>
              </>
            )}
            <span className="bt-service-card__sep">·</span>
            <span className="bt-service-card__price">{priceLabel}</span>
          </div>
        </div>
        {isBookable ? (
          <button type="button" className="bt-cta bt-cta--small" onClick={(): void => onSelect(prestation)}>
            {labels.ride_book_cta}
          </button>
        ) : (
          <span className="bt-service-card__unavailable">{labels.unavailable_no_slot}</span>
        )}
      </div>
    </div>
  );
}
